import {Palette} from 'hubbl-shared';
import React, {memo, useEffect, useRef, useState} from 'react';
import {Control} from 'react-hook-form';
import styled from 'styled-components';
import {AssetIconAI} from '../../assets/icons/icon-ai/AssetIconAI';
import {PrimaryButton} from '../buttons/primary-button/PrimaryButton';
import {SecondaryButton} from '../buttons/secondary-button/SecondaryButton';
import {PaddingBottom} from '../padding/PaddingBottom';
import {TextHeading10} from '../text/TextHeading10';
import {TextHeading7} from '../text/TextHeading7';
import {ImageSelectorInput} from './ImageSelectorInput';
import {useI18n} from '../../hooks';

interface ImageSelectorWithAIProps {
  control: Control<any>;
  controlKey: string;
  isRequiredMessage?: string;
  onFileChange: (file: File) => void;
  externalLoading?: boolean;
  // AI image generation props
  aiImagePrompt: string;
  onAiImagePromptChange: (prompt: string) => void;
  onAiImageGenerate: () => void;
  isAiImageGenerating: boolean;
  aiImageError?: string;
  isAiImagePanelOpen: boolean;
  onToggleAiImagePanel: () => void;
}

const AIImageSection = styled.div<{$open: boolean}>`
  position: relative;
  border-radius: 24px;
  padding: ${({$open}) => ($open ? '18px' : '0')};
  background: ${({$open}) =>
    $open
      ? 'linear-gradient(180deg, rgba(255,255,255,0.72), rgba(255,255,255,0.54))'
      : 'transparent'};
  border: ${({$open}) =>
    $open ? `0px solid ${Palette.almostBlackTransparent10}` : 'none'};
  box-shadow: ${({$open}) =>
    $open
      ? '0 14px 40px rgba(0,0,0,0.14), 0 2px 0 rgba(255,255,255,0.5) inset'
      : 'none'};
  backdrop-filter: ${({$open}) => ($open ? 'blur(8px)' : 'none')};
  transition: all 280ms ease;
  overflow: hidden;
  margin-top: 8px;

  /* Animated gradient ring */
  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: inherit;
    opacity: ${({$open}) => ($open ? 1 : 0)};
    transition: opacity 320ms ease;
    background:
      radial-gradient(
        ellipse 200px 100px at 20% 30%,
        rgba(173, 216, 230, 0.4),
        transparent 60%
      ),
      radial-gradient(
        ellipse 150px 80px at 80% 70%,
        rgba(221, 160, 221, 0.4),
        transparent 50%
      ),
      radial-gradient(
        ellipse 120px 60px at 50% 20%,
        rgba(240, 248, 255, 0.3),
        transparent 40%
      ),
      conic-gradient(
        from 0deg,
        rgba(173, 216, 230, 0.3),
        rgba(221, 160, 221, 0.25),
        rgba(240, 248, 255, 0.2),
        rgba(173, 216, 230, 0.3),
        transparent 60%
      );
    filter: blur(20px);
    z-index: -1;
    animation: ${({$open}) =>
      $open ? 'aliveGlow 10s ease-in-out infinite' : 'none'};
  }

  @keyframes aliveGlow {
    0%,
    100% {
      transform: rotate(0deg) scale(1) translateX(0px) translateY(0px);
      filter: blur(24px) hue-rotate(0deg);
    }
    25% {
      transform: rotate(45deg) scale(1.02) translateX(2px) translateY(-1px);
      filter: blur(22px) hue-rotate(5deg);
    }
    50% {
      transform: rotate(90deg) scale(0.98) translateX(-1px) translateY(2px);
      filter: blur(26px) hue-rotate(10deg);
    }
    75% {
      transform: rotate(135deg) scale(1.01) translateX(1px) translateY(-1px);
      filter: blur(23px) hue-rotate(5deg);
    }
  }

  /* Hide content when closed */
  max-height: ${({$open}) => ($open ? '500px' : '0px')};
  opacity: ${({$open}) => ($open ? 1 : 0)};
  transform: ${({$open}) => ($open ? 'translateY(0)' : 'translateY(-10px)')};
`;

const AIImagePromptContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 4px;
`;

const AIImageTextarea = styled.textarea<{$disabled?: boolean}>`
  width: 100%;
  min-height: 120px;
  padding: 16px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.7)
  );
  backdrop-filter: blur(12px);
  color: ${Palette.almostBlack};
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  transition: all 600ms ease;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  box-sizing: border-box;

  &::placeholder {
    color: ${Palette.almostBlack750};
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 12px 40px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  &:focus {
    box-shadow:
      0 16px 48px rgba(173, 216, 230, 0.15),
      0 0 0 3px rgba(173, 216, 230, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.5),
      rgba(255, 255, 255, 0.3)
    );
  }
`;

const AIImageButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 4px;
`;

const AIImageRightButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Floating AI Button (similar to FloatingToolbarButton)
const FloatingAIButton = styled.button<{$breathing?: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${Palette.primaryCherokee} 0%,
    ${Palette.primaryTerraCotta} 100%
  );
  border: 1px solid ${Palette.primaryCherokee};
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(255, 110, 48, 0.35);
  padding: 8px;
  cursor: pointer;
  width: 40px;
  min-width: 40px;
  min-height: 44px;
  top: 8px;
  position: absolute;
  right: 12px;

  z-index: 10;
  overflow: visible;
  transition:
    background 0.2s,
    transform 0.2s,
    box-shadow 0.2s;
  color: #222;
  animation: ${({$breathing}) =>
    $breathing ? 'breathing 2s ease-in-out infinite' : 'none'};

  @keyframes breathing {
    0%,
    100% {
      transform: scale(1);
      box-shadow: 0 10px 25px rgba(255, 110, 48, 0.35);
    }
    50% {
      transform: scale(1.1);
      box-shadow: 0 15px 35px rgba(255, 110, 48, 0.5);
    }
  }

  &:hover {
    background: linear-gradient(
      135deg,
      ${Palette.primaryCherokee} 0%,
      ${Palette.primaryTerraCotta} 100%
    );
    transform: scale(1.1);
    box-shadow: 0 15px 35px rgba(255, 110, 48, 0.5);
  }

  /* Soft outer glow */
  &::after {
    content: '';
    pointer-events: none;
    position: absolute;
    inset: -6px;
    border-radius: 20px;
    opacity: 1;
    background: radial-gradient(
      500px 120px at 50% 50%,
      rgba(255, 149, 0, 0.18),
      transparent
    );
    filter: blur(10px);
    transition: opacity 0.2s ease;
  }
`;

const ImageSelectorContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AITooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  padding: 6px 10px;
  background-color: ${Palette.almostBlack};
  color: ${Palette.white};
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease-in-out;
  z-index: 1000;
  pointer-events: none;

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    right: 12px;
    border-width: 4px;
    border-style: solid;
    border-color: ${Palette.almostBlack} transparent transparent transparent;
  }
`;

const FloatingAIButtonContainer = styled.div`
  &:hover .ai-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

export const ImageSelectorWithAI: React.FC<ImageSelectorWithAIProps> = memo(
  ({
    control,
    controlKey,
    isRequiredMessage,
    onFileChange,
    externalLoading = false,
    aiImagePrompt,
    onAiImagePromptChange,
    onAiImageGenerate,
    isAiImageGenerating,
    aiImageError,
    isAiImagePanelOpen,
    onToggleAiImagePanel,
  }) => {
    const t = useI18n();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    const canGenerateImage =
      !isAiImageGenerating && Boolean(aiImagePrompt.trim());

    // Function to trigger accordion height recalculation
    const triggerAccordionRecalculation = () => {
      // Dispatch a custom event that the accordion listens for
      window.dispatchEvent(new CustomEvent('accordion-content-change'));
    };

    // Trigger recalculation when AI panel opens/closes
    useEffect(() => {
      triggerAccordionRecalculation();
    }, [isAiImagePanelOpen]);

    // Trigger recalculation when image generation state changes
    useEffect(() => {
      if (!isAiImageGenerating) {
        // Small delay to ensure DOM has updated after image generation
        const timeoutId = setTimeout(() => {
          triggerAccordionRecalculation();
        }, 200);
        return () => clearTimeout(timeoutId);
      }
    }, [isAiImageGenerating]);

    // Trigger recalculation when external loading state changes
    useEffect(() => {
      if (!externalLoading) {
        // Small delay to ensure DOM has updated after image loading
        const timeoutId = setTimeout(() => {
          triggerAccordionRecalculation();
        }, 200);
        return () => clearTimeout(timeoutId);
      }
    }, [externalLoading]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canGenerateImage) {
        e.preventDefault();
        onAiImageGenerate();
      }
    };

    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: '0px'}}>
        {/* AI Image Generation Panel */}
        <AIImageSection $open={isAiImagePanelOpen}>
          <AIImagePromptContainer>
            <TextHeading7 text={t.ai.imageGeneration.title} />
            <AIImageTextarea
              ref={textareaRef}
              value={aiImagePrompt}
              onChange={e => onAiImagePromptChange(e.target.value)}
              placeholder={t.ai.imageGeneration.placeholder}
              disabled={isAiImageGenerating}
              $disabled={isAiImageGenerating}
              onKeyDown={handleKeyDown}
            />

            {aiImageError && (
              <TextHeading10
                text={aiImageError}
                color={Palette.systemError}
                style={{marginTop: 4}}
              />
            )}

            <AIImageButtons>
              <div style={{fontSize: '12px', color: Palette.almostBlack750}}>
                {t.ai.imageGeneration.keyboardShortcut}
              </div>
              <AIImageRightButtons>
                <SecondaryButton
                  label={t.ai.imageGeneration.clear}
                  onClick={() => onAiImagePromptChange('')}
                  isDisabled={isAiImageGenerating || !aiImagePrompt}
                  isCompact
                  customWidth={80}
                />
                <PrimaryButton
                  label={
                    isAiImageGenerating
                      ? t.ai.imageGeneration.generating
                      : t.ai.imageGeneration.generateImage
                  }
                  onClick={onAiImageGenerate}
                  isDisabled={!canGenerateImage}
                  isCompact
                  customWidth={120}
                />
              </AIImageRightButtons>
            </AIImageButtons>
          </AIImagePromptContainer>
        </AIImageSection>
        {isAiImagePanelOpen && <PaddingBottom amount={16} />}

        {/* Image Selector with Floating AI Button */}
        <ImageSelectorContainer
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}>
          <ImageSelectorInput
            control={control}
            controlKey={controlKey}
            isRequiredMessage={isRequiredMessage}
            onFileChange={onFileChange}
            externalLoading={externalLoading || isAiImageGenerating}
          />

          {/* Floating AI Button */}
          <FloatingAIButtonContainer>
            <FloatingAIButton
              onClick={onToggleAiImagePanel}
              $breathing={!isAiImagePanelOpen && isHovered}
              aria-label="Generate AI Image">
              <AssetIconAI color={Palette.almostBlack} size={16} />
            </FloatingAIButton>
            <AITooltip className="ai-tooltip">
              {isAiImagePanelOpen
                ? t.ai.imageGeneration.tooltip.hide
                : t.ai.imageGeneration.tooltip.generate}
            </AITooltip>
          </FloatingAIButtonContainer>
        </ImageSelectorContainer>
      </div>
    );
  },
);
